/**
 * Asignaciones (dentro del área de Créditos): "Asignar créditos" es la vía rápida
 * universal para acreditar créditos a CUALQUIER usuario (padre o terapeuta) con
 * justificación obligatoria y comprobante opcional (obligatorio si es pago por
 * transferencia; reusa el bucket payment-receipts de #45). Registro inmutable:
 * cada asignación es una fila del libro mayor + audit_log; corregir un error es un
 * ajuste negativo nuevo, nunca un borrado. Aquí se ve el historial filtrable.
 */
import { useState } from "react";
import { adminApi } from "../../api/endpoints.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import type { JustificacionAsignacion, UsuarioAdmin } from "../../api/types.js";
import { Async, fmtFechaCorta } from "./ui.js";
import { VolverLicencias } from "./AdminPagosOffline.js";
import s from "./admin.module.css";

const JUSTIFICACIONES: JustificacionAsignacion[] = [
  "pago_por_transferencia",
  "cortesia",
  "correccion",
  "promocion",
  "otro",
];

const JUST_LABEL: Record<JustificacionAsignacion, string> = {
  pago_por_transferencia: "Pago por transferencia",
  cortesia: "Cortesía",
  correccion: "Corrección",
  promocion: "Promoción",
  otro: "Otro",
};

export function AdminAsignaciones() {
  const [justificacionTipo, setJustificacionTipo] = useState<"" | JustificacionAsignacion>("");
  const [usuarioId, setUsuarioId] = useState("");
  const [mostrarForm, setMostrarForm] = useState(false);

  const q = useQuery(
    () =>
      adminApi.asignaciones({
        ...(justificacionTipo ? { justificacionTipo } : {}),
        ...(usuarioId ? { usuarioId } : {}),
        limit: 200,
      }),
    [justificacionTipo, usuarioId],
  );

  return (
    <>
      <VolverLicencias />
      <div className={s.sectionTitle}>Asignaciones</div>

      <div className={s.toolbar}>
        <select
          className={s.select}
          value={justificacionTipo}
          onChange={(e) => setJustificacionTipo(e.target.value as "" | JustificacionAsignacion)}
        >
          <option value="">Toda justificación</option>
          {JUSTIFICACIONES.map((j) => (
            <option key={j} value={j}>
              {JUST_LABEL[j]}
            </option>
          ))}
        </select>
        <input
          className={s.search}
          placeholder="Filtrar por usuarioId"
          value={usuarioId}
          onChange={(e) => setUsuarioId(e.target.value)}
        />
        {usuarioId ? (
          <button className={s.btn} onClick={() => setUsuarioId("")}>
            Quitar filtro
          </button>
        ) : null}
        <span className={s.headerSpacer} />
        <button className={`${s.btn} ${s.btnPrimary}`} onClick={() => setMostrarForm((v) => !v)}>
          {mostrarForm ? "Cerrar" : "Asignar créditos"}
        </button>
      </div>

      {mostrarForm ? (
        <FormAsignar
          onHecho={() => {
            setMostrarForm(false);
            q.reload();
          }}
        />
      ) : null}

      <div className={s.panel}>
        <Async q={q}>
          {(data) => (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Beneficiario</th>
                    <th>Créditos</th>
                    <th>Justificación</th>
                    <th>Detalle</th>
                    <th>Asignó</th>
                    <th>Comprobante</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((a) => (
                    <tr key={a.id}>
                      <td className={s.muted}>{fmtFechaCorta(a.createdAt)}</td>
                      <td>
                        {a.usuarioNombre ?? <span className={s.mono}>{a.usuarioId.slice(0, 8)}</span>}
                        {a.usuarioEmail ? <div className={s.muted}>{a.usuarioEmail}</div> : null}
                      </td>
                      <td>
                        <strong>+{a.cantidad}</strong>
                      </td>
                      <td>{JUST_LABEL[a.justificacionTipo]}</td>
                      <td className={s.muted}>{a.detalle ?? "—"}</td>
                      <td className={s.muted}>{a.asignadoPorNombre ?? "—"}</td>
                      <td>
                        {a.comprobanteUrl ? (
                          <a className={s.btn} href={a.comprobanteUrl} target="_blank" rel="noreferrer">
                            Ver
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={s.empty}>
                        Sin asignaciones con esos filtros.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </Async>
      </div>
    </>
  );
}

function FormAsignar({ onHecho }: { onHecho: () => void }) {
  const usuariosQ = useQuery(() => adminApi.usuarios(), []);
  const [usuarioId, setUsuarioId] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [justificacionTipo, setJustificacionTipo] = useState<JustificacionAsignacion>("cortesia");
  const [detalle, setDetalle] = useState("");
  const [archivo, setArchivo] = useState<File | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const beneficiarios = (usuariosQ.data ?? []).filter(
    (u: UsuarioAdmin) => u.rol === "terapeuta" || u.rol === "padre",
  );
  const requiereComprobante = justificacionTipo === "pago_por_transferencia";
  const requiereDetalle = justificacionTipo === "otro";

  async function enviar() {
    setError(null);
    const n = Number(cantidad);
    if (!usuarioId) return setError("Elige un beneficiario (terapeuta o padre).");
    if (!Number.isInteger(n) || n <= 0) return setError("La cantidad debe ser un entero positivo.");
    if (requiereDetalle && !detalle.trim()) return setError("El detalle es obligatorio para 'Otro'.");
    if (requiereComprobante && !archivo) {
      return setError("El comprobante es obligatorio para un pago por transferencia.");
    }
    const form = new FormData();
    form.append("usuarioId", usuarioId);
    form.append("cantidad", String(n));
    form.append("justificacionTipo", justificacionTipo);
    if (detalle.trim()) form.append("detalle", detalle.trim());
    if (archivo) form.append("file", archivo);

    setOcupado(true);
    try {
      await adminApi.asignarCreditos(form);
      onHecho();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className={`${s.panel} ${s.panelPad}`} style={{ marginBottom: 12 }}>
      <div className={s.dt}>Asignar créditos</div>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
      <div className={s.dl} style={{ marginTop: 8 }}>
        <label>
          <div className={s.dt}>Beneficiario (terapeuta o padre)</div>
          <select className={s.select} value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)}>
            <option value="">— elegir —</option>
            {beneficiarios.map((u: UsuarioAdmin) => (
              <option key={u.id} value={u.id}>
                [{u.rol}] {u.nombre} · {u.email}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div className={s.dt}>Cantidad (créditos)</div>
          <input
            className={s.search}
            type="number"
            min="1"
            step="1"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
          />
        </label>
        <label>
          <div className={s.dt}>Justificación</div>
          <select
            className={s.select}
            value={justificacionTipo}
            onChange={(e) => setJustificacionTipo(e.target.value as JustificacionAsignacion)}
          >
            {JUSTIFICACIONES.map((j) => (
              <option key={j} value={j}>
                {JUST_LABEL[j]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div className={s.dt}>
            Detalle{requiereDetalle ? " (obligatorio)" : " (opcional)"}
          </div>
          <input className={s.search} value={detalle} onChange={(e) => setDetalle(e.target.value)} />
        </label>
        <label>
          <div className={s.dt}>
            Comprobante {requiereComprobante ? "(obligatorio)" : "(opcional)"} — png/jpg/webp/pdf, máx 5MB
          </div>
          <input
            className={s.search}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
        </label>
      </div>
      <div className={s.btnRow} style={{ marginTop: 12 }}>
        <button className={`${s.btn} ${s.btnPrimary}`} disabled={ocupado} onClick={() => void enviar()}>
          {ocupado ? "Asignando…" : "Asignar créditos"}
        </button>
      </div>
    </div>
  );
}
